import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { ProjectSocketEvent } from '@trinity/chat';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { useProjectSocket } from '@trinity/chat-components/websocket/hooks/useProjectSocket.js';
import { useProjectConferenceTagSummaries } from '@/utils/api';
import { RefreshSummariesContext } from './context';

type Props =
  IProjectId &
  ChildrenProps;

type Key = {
  projectId: number;
  tagId: number;
};

export const ProjectSummaryRefreshContainer = (props: Props) => {
  const api = useChatApiClient();

  const [websocketKey, setWebsocketKey] = useState<Key>();

  const {
    isPending: isGenerating,
    mutateAsync: refresh,
  } = useMutation({
    mutationKey: ['post:projects/conference-tags/summaries/generate', props.projectId],
    mutationFn: (tagId?: number) => {
      return api.projects.generateTagSummaries({
        projectId: props.projectId,
        tagIds: tagId ? [tagId] : undefined,
      });
    }, onError: () => {
      setWebsocketKey(null);
    }, onMutate: (tagId?: number) => {
      setWebsocketKey({
        projectId: props.projectId,
        tagId,
      });
    },
  });

  const { refetch: refetchProjectTagSummaries, isLoading: isLoadingSummaries } = useProjectConferenceTagSummaries({ projectId: props.projectId });

  const handleTagSummariesAvailable = useCallback(() => {
    refetchProjectTagSummaries();
    setWebsocketKey(null);
  }, [refetchProjectTagSummaries]);

  const socket = useProjectSocket(props.projectId);

  useEffect(() => {
    if (socket) {
      socket.on(ProjectSocketEvent.TagSummariesAvailable, handleTagSummariesAvailable);
      return () => {
        socket.off(ProjectSocketEvent.TagSummariesAvailable, handleTagSummariesAvailable);
      };
    }
  }, [socket, handleTagSummariesAvailable]);
  /*
  useEffect(() => {
    const key = websocketKey;
    if (key) {
      $ws.projects.subscribe(key.projectId);
      $ws.projects.open();

      return () => {
        $ws.projects.unsubscribe(key.projectId);
        $ws.projects.close();
      };
    }
  }, [websocketKey]);
  */
  const isRefreshing = useMemo(() => {
    return isGenerating || isLoadingSummaries || !!websocketKey;
  }, [
    isGenerating,
    isLoadingSummaries,
    websocketKey,
  ]);

  const ctx = useMemo(() => ({
    isRefreshing,
    refresh,
    tagRefreshing: websocketKey?.tagId,
  }), [
    isRefreshing,
    refresh,
    websocketKey,
  ]);

  return (
    <RefreshSummariesContext.Provider value={ctx}>
      {props.children}
    </RefreshSummariesContext.Provider>
  );
};